import React, { useMemo } from 'react';
import { CheckBadgeIcon } from '@heroicons/react/20/solid';
import {
  ArrowPathIcon,
  BoltIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  CubeTransparentIcon,
  DocumentCheckIcon,
  EyeIcon,
  FlagIcon,
  HeartIcon,
  PhotoIcon,
  ScaleIcon,
  TrophyIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import type { BuildStat } from '../../types/buildExpansion';

/**
 * Adaptive build telemetry panel. Layout recomposes by count (1-8) so every
 * preference set reads as a deliberate instrument cluster, not a sparse row.
 */

const MAX_STATS = 8;

/** Keys that earn featured (hero) treatment when the mosaic has a hero slot. */
const FEATURE_KEYS = new Set([
  'power',
  'torque',
  'weight',
  'power_to_weight',
  'best_performance',
  'documentation_score',
]);

const STAT_ICONS: Record<string, typeof BoltIcon> = {
  power: BoltIcon,
  torque: ArrowPathIcon,
  weight: ScaleIcon,
  power_to_weight: ChartBarIcon,
  best_performance: FlagIcon,
  modification_count: WrenchScrewdriverIcon,
  documentation_score: DocumentCheckIcon,
  media_count: PhotoIcon,
  award_count: TrophyIcon,
  event_count: CalendarDaysIcon,
  years_documented: ClockIcon,
  view_count: EyeIcon,
  favorite_count: HeartIcon,
};

export interface BuildStatsStripProps {
  stats: BuildStat[];
  className?: string;
}

type ParsedValue = {
  main: string;
  unit: string | null;
  /** 0-100 when value looks like a score (e.g. 72/100). */
  scorePercent: number | null;
};

function parseStatValue(raw: string): ParsedValue {
  const value = raw.trim();
  if (!value) return { main: '', unit: null, scorePercent: null };

  const scoreMatch = value.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
  if (scoreMatch) {
    const num = parseFloat(scoreMatch[1]);
    const den = parseFloat(scoreMatch[2]);
    return {
      main: scoreMatch[1],
      unit: `/ ${scoreMatch[2]}`,
      scorePercent: den > 0 ? Math.min(100, Math.max(0, (num / den) * 100)) : null,
    };
  }

  const unitMatch = value.match(
    /^([+-]?\d[\d,]*(?:\.\d+)?)\s+([a-zA-Z°%]+(?:[/-][a-zA-Z]+)?)$/
  );
  if (unitMatch) {
    return { main: unitMatch[1], unit: unitMatch[2], scorePercent: null };
  }

  // Time-like or mixed display (e.g. 1:48.2) stays intact.
  return { main: value, unit: null, scorePercent: null };
}

function pickFeaturedIndex(stats: BuildStat[]): number {
  const preferred = stats.findIndex((s) => FEATURE_KEYS.has(s.key));
  return preferred >= 0 ? preferred : 0;
}

/** Odd counts + single get a hero tile; even counts stay equal mosaics. */
function usesHeroLayout(count: number): boolean {
  return count === 1 || count % 2 === 1;
}

function orderForDisplay(stats: BuildStat[], hero: boolean): BuildStat[] {
  if (!hero || stats.length <= 1) return stats;
  const featuredIndex = pickFeaturedIndex(stats);
  if (featuredIndex <= 0) return stats;
  const next = [...stats];
  const [featured] = next.splice(featuredIndex, 1);
  return [featured, ...next];
}

/**
 * Span classes assume a 6-column grid from `sm` up (2-col on phones).
 * Count 8 widens to 12 columns at `lg`.
 */
function cellSpanClass(count: number, index: number, hero: boolean): string {
  switch (count) {
    case 1:
      return 'col-span-2 sm:col-span-6';
    case 2:
      return 'col-span-1 sm:col-span-3';
    case 3:
      // Hero full width on phones; equal thirds from sm.
      if (hero && index === 0) return 'col-span-2 sm:col-span-2';
      return 'col-span-1 sm:col-span-2';
    case 4:
      return 'col-span-1 sm:col-span-3';
    case 5:
      // Hero tall on the left from sm; full-width lead on phones.
      if (hero && index === 0) return 'col-span-2 sm:col-span-3 sm:row-span-2';
      return 'col-span-1 sm:col-span-3';
    case 6:
      return 'col-span-1 sm:col-span-2';
    case 7:
      // Hero banner, then a tidy 3x2 of the rest.
      if (hero && index === 0) return 'col-span-2 sm:col-span-6';
      return 'col-span-1 sm:col-span-2';
    case 8:
      return 'col-span-1 sm:col-span-3 lg:col-span-3';
    default:
      return 'col-span-1 sm:col-span-2';
  }
}

function gridClassForCount(count: number): string {
  if (count === 8) return 'grid-cols-2 sm:grid-cols-6 lg:grid-cols-12';
  if (count === 5) return 'grid-cols-2 sm:grid-cols-6 sm:auto-rows-fr';
  return 'grid-cols-2 sm:grid-cols-6';
}

function DocScoreRing({ percent, size }: { percent: number; size: 'sm' | 'lg' }) {
  const r = size === 'lg' ? 22 : 16;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  const dim = size === 'lg' ? 56 : 40;
  const stroke = size === 'lg' ? 4 : 3;
  return (
    <svg
      width={dim}
      height={dim}
      viewBox={`0 0 ${dim} ${dim}`}
      className="shrink-0 text-[color:var(--build-accent,#F97316)]"
      aria-hidden
    >
      <circle
        cx={dim / 2}
        cy={dim / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.15}
        strokeWidth={stroke}
      />
      <circle
        cx={dim / 2}
        cy={dim / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${dim / 2} ${dim / 2})`}
        className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-700 motion-safe:ease-out"
      />
    </svg>
  );
}

function StatMetricIcon({
  statKey,
  size,
}: {
  statKey: string;
  size: 'sm' | 'lg';
}) {
  const Icon = STAT_ICONS[statKey] ?? CubeTransparentIcon;
  return (
    <span
      className={`shrink-0 inline-flex items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-[color:var(--build-accent,#F97316)] ${
        size === 'lg' ? 'h-12 w-12' : 'h-9 w-9'
      }`}
      aria-hidden
      data-testid={`stat-icon-${statKey}`}
    >
      <Icon className={size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'} strokeWidth={1.75} />
    </span>
  );
}

function StatCell({
  stat,
  featured,
  solo,
}: {
  stat: BuildStat;
  featured: boolean;
  solo: boolean;
}) {
  const parsed = parseStatValue(stat.value);
  const showRing = stat.key === 'documentation_score' && parsed.scorePercent != null;
  const iconSize = featured || solo ? 'lg' : 'sm';

  const valueSize = solo
    ? 'text-4xl sm:text-5xl'
    : featured
      ? 'text-3xl sm:text-4xl'
      : 'text-2xl sm:text-[1.75rem]';

  return (
    <div
      className={`build-stat-cell relative flex h-full min-w-0 flex-col justify-center overflow-hidden rounded-xl border border-white/[0.06] px-3 py-4 sm:px-4 sm:py-5 ${
        featured || solo
          ? 'bg-gradient-to-br from-white/[0.06] via-[#0e0e0e] to-[#0a0a0a] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
          : 'bg-[#0a0a0a]/55'
      }`}
    >
      <div className="build-stat-cell-accent" aria-hidden />

      <div
        className={`flex items-center gap-3 min-w-0 ${
          solo ? 'justify-center sm:justify-start' : ''
        }`}
      >
        {showRing ? (
          <DocScoreRing percent={parsed.scorePercent!} size={iconSize} />
        ) : (
          <StatMetricIcon statKey={stat.key} size={iconSize} />
        )}

        <div className={`min-w-0 ${solo ? 'text-center sm:text-left' : ''}`}>
          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span
              className={`font-display font-semibold tracking-tight text-white tabular-nums leading-none mod-text-wrap ${valueSize}`}
            >
              {parsed.main}
            </span>
            {parsed.unit ? (
              <span
                className={`font-medium tabular-nums text-zinc-400 mod-text-wrap ${
                  featured || solo ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'
                }`}
              >
                {parsed.unit}
              </span>
            ) : null}
            {stat.verified ? (
              <span className="inline-flex self-center" title="Verified">
                <CheckBadgeIcon className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
                <span className="sr-only">Verified</span>
              </span>
            ) : null}
          </div>

          <p
            className={`mt-2 font-medium uppercase tracking-[0.14em] text-[10px] sm:text-[11px] mod-text-wrap ${
              featured || solo ? 'text-zinc-300' : 'text-zinc-500'
            }`}
          >
            {stat.label}
          </p>

          {stat.detail ? (
            <p className="mt-1 text-[11px] text-zinc-500 leading-snug mod-text-wrap">{stat.detail}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export const BuildStatsStrip: React.FC<BuildStatsStripProps> = ({ stats, className = '' }) => {
  const shown = useMemo(
    () => stats.filter((s) => s.value != null && String(s.value).trim() !== '').slice(0, MAX_STATS),
    [stats]
  );

  const count = shown.length;
  const hero = usesHeroLayout(count);
  const ordered = useMemo(() => orderForDisplay(shown, hero), [shown, hero]);

  if (count === 0) return null;

  return (
    <section
      className={`build-stats-infographic min-w-0 overflow-x-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#121212] to-[#0a0a0a] p-3 sm:p-4 ${className}`}
      aria-label="Build stats"
      data-count={count}
    >
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2 px-0.5">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-500">
            Build telemetry
          </p>
          <p className="mt-0.5 text-sm text-zinc-400 break-words">
            {count === 1
              ? 'Signature metric for this build'
              : `${count} live stats from this build`}
          </p>
        </div>
        <p className="text-xs tabular-nums text-zinc-600" aria-hidden>
          {count}/{MAX_STATS}
        </p>
      </header>

      <ul className={`grid gap-2 sm:gap-2.5 list-none p-0 m-0 min-w-0 ${gridClassForCount(count)}`}>
        {ordered.map((stat, index) => {
          const featured = hero && index === 0;
          const solo = count === 1;
          return (
            <li key={stat.key} className={`min-w-0 ${cellSpanClass(count, index, hero)}`}>
              <StatCell stat={stat} featured={featured} solo={solo} />
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default BuildStatsStrip;
